/*
 * Jira Ticket:
 * Created Date: Tue, 17th Mar 2020, 14:12:23 pm
 * Author: Jessica Mowatt
 * Email: jessica.mowatt@thedistance.co.uk
 * Copyright (c) 2020 The Distance
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  Create,
  FormDataConsumer,
  NumberInput,
  required,
  ReferenceInput,
  SelectInput,
  SimpleForm,
  TextInput,
  useNotify,
  useTranslate,
  usePermissions,
  Title,
  useDataProvider,
  fetchStart,
  fetchEnd,
} from 'react-admin';
import debounce from 'lodash.debounce';
import { AccessDenied } from 'Layout';
import { isProjectManager } from 'utils/roles';
import { useForm } from 'react-final-form';
import { TextButton as SearchButton } from 'Components/Buttons';
import MapComponent from 'Components/Map';
import { columnStyles } from 'styles';
import { getUserLocation } from 'utils';
import * as R from 'ramda';
import { useDispatch } from 'react-redux';
import { usePersistentOrganisation } from '../../Components/Contexts';
import getOrganisationInfo from '../../utils/getOrganisationInfo';

const OrganisationProjectSelector = ({
  organisationId,
  setOrganisationId,
  organisationChoices,
}) => {
  const form = useForm();

  return (
    <>
      <SelectInput
        label="resources.Site.fields.organisation"
        source="organisationId"
        perPage={1000}
        choices={organisationChoices}
        initialValue={organisationId}
        onChange={event => {
          setOrganisationId(event.target.value);
          form.change('project.id', null);
        }}
        validate={required()}
      />
      <ReferenceInput
        label="resources.Site.fields.project"
        source="project.id"
        reference="Project"
        filter={{ organisationId }}
        perPage={1000}
        sort={{ field: 'name', order: 'ASC' }}
        validate={required()}
        disabled={!organisationId}
      >
        <SelectInput source="id" optionText="name" />
      </ReferenceInput>
    </>
  );
};

const SanitizedForm = ({
  notify,
  organisationChoices,
  organisationId,
  setOrganisationId,
}) => {
  const mapRef = useRef();
  const translate = useTranslate();
  const classes = columnStyles();
  const form = useForm();
  const [mapLoaded, setMapLoaded] = useState(false);
  const [userLocation, setUserLocation] = useState({
    lat: 51.5074, // center on London initially
    lng: 0.1278,
  });

  const onSearchClick = value => {
    if (value && value.trim()) {
      const geocodingParams = {
        address: value,
      };
      return mapRef.current
        .performSearch(geocodingParams)
        .then(searchResult => form.change('location', searchResult))
        .catch(e => {
          if (process.env.NODE_ENV === 'development') console.error(e);
          if (e.messageArgs && e.messageArgs.hasOwnProperty('address')) {
            return notify(e.message, 'warning', {
              ...e.messageArgs,
            });
          }
          return notify(e.message, 'warning', {
            status: e.messageArgs,
          });
        });
    }
    return notify('error.maps.emptySearch', 'warning');
  };

  const delayedOnSearchClick = useRef(
    debounce(value => onSearchClick(value), 1000)
  ).current;

  const onMapClick = coordinates => form.change('location', coordinates);

  useEffect(() => {
    let isSubscribed = true;
    const tryGetUserLocation = () =>
      getUserLocation()
        .then(result => {
          if (isSubscribed)
            setUserLocation({
              lat: result.coords.latitude,
              lng: result.coords.longitude,
            });
        })
        .catch(e => {
          if (process.env.NODE_ENV === 'development') console.error(e);
          return setUserLocation({
            lat: 55.3781,
            lng: 3.436,
          });
        });
    tryGetUserLocation();
    return () => (isSubscribed = false);
  }, []);

  return (
    <div className={classes.root}>
      <div className={classes.column}>
        <TextInput
          source="name"
          validate={required()}
          label="resources.Site.fields.siteName"
        />
        <FormDataConsumer>
          {formDataProps => (
            <OrganisationProjectSelector
              organisationId={organisationId}
              setOrganisationId={setOrganisationId}
              organisationChoices={organisationChoices}
              {...formDataProps}
            />
          )}
        </FormDataConsumer>

        <TextInput
          source="postCode"
          label="resources.Site.fields.postCode"
          validate={required()}
        />
        <NumberInput
          source="location.lat"
          validate={required()}
          label="resources.Site.fields.latitude"
          disabled={mapLoaded}
        />
        <NumberInput
          source="location.lng"
          validate={required()}
          label="resources.Site.fields.longitude"
          disabled={mapLoaded}
        />
      </div>
      <div className={classes.centeredMiddleColumnLower}>
        <FormDataConsumer>
          {({ formData }) => (
            <SearchButton
              label={translate('actions.search')}
              onClick={() => delayedOnSearchClick(formData.postCode)}
            />
          )}
        </FormDataConsumer>
      </div>
      <div className={classes.lastSiteColumn}>
        <MapComponent
          ref={mapRef}
          mapZoom={17}
          defaultMapZoom={10}
          defaultCenter={userLocation}
          setMapLoaded={setMapLoaded}
          updateFormValues={coords => onMapClick(coords)}
        />
      </div>
    </div>
  );
};

const SiteCreate = props => {
  const { permissions } = usePermissions();
  const classes = columnStyles();
  const notify = useNotify();
  const dispatch = useDispatch();
  const dataProvider = useDataProvider();
  const { persistentOrganisation } = usePersistentOrganisation();
  const [organisationId, setOrganisationId] = useState(
    persistentOrganisation.id
  );
  // MARK: - Local
  const [organisations, setOrganisations] = useState([]);
  const [organisationChoices, setOrganisationChoices] = useState([]);

  const rest = {
    classes,
    notify,
    organisationChoices,
    organisationId,
    setOrganisationId,
    ...props,
  };

  // MARK: - Setup

  // Get org selection options
  const getOrganisationOptions = () => {
    dispatch(fetchStart());
    dataProvider('GET_LIST', 'Organisation', {
      pagination: { page: 1, perPage: 1000 },
    })
      .then(result => {
        const orgs = R.path(['data'], result) || [];
        setOrganisations(orgs);
      })
      .catch()
      .finally(() => {
        dispatch(fetchEnd());
      });
    return () => {};
  };

  const useMountEffect = fun => useEffect(fun, []);
  useMountEffect(getOrganisationOptions); // run once

  // Organisations select options based on permissions
  useEffect(() => {
    const choices = organisations
      .filter(org => isProjectManager(permissions, org.id))
      .map(permittedOrg => {
        return { id: permittedOrg.id, name: permittedOrg.name };
      });
    setOrganisationChoices(choices);
  }, [organisations, permissions]);

  // MARK: Rendering

  if (organisationId && !isProjectManager(permissions, organisationId)) {
    return <AccessDenied />;
  }

  return (
    <>
      <Title title="resources.General.cmsTitle" />
      <Create {...props}>
        <SimpleForm
          redirect={
            organisationId ? `/Organisation/${organisationId}/2` : undefined
          }
        >
          <SanitizedForm {...rest} />
        </SimpleForm>
      </Create>
    </>
  );
};

export default SiteCreate;
